import { DatePicker } from "antd";
import { translate } from "shared/languages";
import { now, tzDate, DEFAULT_DATE_FORMAT, DEFAULT_DATE_SUBMIT_FORMAT } from "shared/utils/dayjs";
import { CustomDayRange } from "shared/types";

const { RangePicker } = DatePicker;

interface CustomRangePickerProps {
  value: CustomDayRange;
  onChange: (value: CustomDayRange) => void;
}

// Day-precision custom-range picker, matching PackageComparisonContent.tsx's
// (Bandingkan) day-range picker exactly — same plain connected RangePicker,
// same dual-month popup, no custom pill/layout/cell highlighting (that
// component is left untouched; this is a new shared sibling for every OTHER
// report's day-precision picker). A connected RangePicker needs none of the
// independent-fields bookkeeping a from/to pair of separate pickers would
// (pending state, order-independent sorting, a hand-rolled "reference cell"
// hint for the other side) — AntD already enforces chronological order and
// shows its own in-range preview natively, so onChange delivers
// [start, end] in the right order once both are picked.
const CustomRangePicker = ({ value, onChange }: CustomRangePickerProps) => (
  // The popup's month titles and weekdays follow the admin's language, like
  // every date on the reports since 2026-10-05 (this picker used to force
  // AntD's English locale to match the reports' then-English months).
  // Placeholders are set explicitly below, same i18n keys as the (Bandingkan)
  // picker in PackageComparisonContent.tsx.
  <RangePicker
    // A fixed (not "100%") width, unlike PackageComparisonContent.tsx's
    // (Bandingkan) RangePicker — that one sits alone in its row, but this
    // picker shares a wrapping flex row with RangeSegmented on every other
    // report page, and width:"100%" would claim the whole row (its flex
    // basis), pushing the segmented pills onto their own line below instead
    // of sitting inline beside it.
    style={{ width: 260 }}
    format={DEFAULT_DATE_FORMAT}
    placeholder={[
      translate("analyticsPage.sales.custom_day_range_from"),
      translate("analyticsPage.sales.custom_day_range_to"),
    ]}
    value={value ? [tzDate(value.from), tzDate(value.to)] : null}
    onChange={(dates) => {
      if (!dates || !dates[0] || !dates[1]) {
        onChange(null);
        return;
      }
      onChange({
        from: dates[0].format(DEFAULT_DATE_SUBMIT_FORMAT),
        to: dates[1].format(DEFAULT_DATE_SUBMIT_FORMAT),
      });
    }}
    disabledDate={(current) => !!current && current > now().endOf("day")}
  />
);

export default CustomRangePicker;
